import { meControlMiniAvatarStyle } from './MeControlLauncher.scss';
import type { MeControlLauncherProps } from './MeControlLauncherProps';
import { Tooltip, Avatar } from '@fluentui/react-components';
import useImageLoading from '../hooks/useImageLoading';
import React from 'react';
import { useState } from 'owa-react';
import { FluentNovaMeControl } from 'owa-fluent-nova-mecontrol';
import { meAvatarTriggerTooltipTextString } from '../mecontrolLauncherStrings.locstring.json';
import loc from 'owa-localize';
import { getDisplayNameAndPhoto } from '../utils/getDisplayNameAndPhoto';

const DEFAULT_AVATAR_SIZE = 28;

const MeControlLauncher: React.FC<MeControlLauncherProps> = ({
    mailboxInfo,
    onOpenMeControl,
    avatarSize = DEFAULT_AVATAR_SIZE,
}) => {
    const [loadFluentNovaMeControl, setLoadFluentNovaMeControl] = useState(false);
    const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);
    const [displayName, setDisplayName] = useState<string>('');
    const isImageLoaded = useImageLoading(photoUrl || '');

    const handleOnClick = React.useCallback(() => {
        if (onOpenMeControl) {
            onOpenMeControl();
        } else {
            setLoadFluentNovaMeControl(true);
        }
    }, [onOpenMeControl]);

    React.useEffect(() => {
        let disposed = false;

        async function loadDisplayNameAndPhoto() {
            const { displayName: name, photoObjectUrl } = await getDisplayNameAndPhoto(
                mailboxInfo,
                48
            );
            if (disposed) {
                return;
            }
            setDisplayName(name || '');
            setPhotoUrl(photoObjectUrl);
        }

        loadDisplayNameAndPhoto();

        return () => {
            disposed = true;
        };
    }, [mailboxInfo.userIdentity]);

    // Only show FluentNovaMeControl if no custom onOpen callback was provided
    if (loadFluentNovaMeControl && !onOpenMeControl) {
        return (
            <React.Suspense fallback={null}>
                <FluentNovaMeControl
                    mailboxInfo={mailboxInfo}
                    initialDisplayName={displayName}
                    initialPhotoUrl={photoUrl}
                />
            </React.Suspense>
        );
    }

    return (
        <Tooltip content={loc(meAvatarTriggerTooltipTextString, displayName)} relationship="label">
            <button
                className={meControlMiniAvatarStyle}
                onClick={handleOnClick}
                aria-label={displayName || ''}
                type="button"
            >
                <Avatar
                    data-tid={'me-control-mini-avatar'}
                    size={avatarSize}
                    aria-hidden="true"
                    // if the image fails to load we want to show the user icon
                    // so, we don't render the name (initials) nor any image (which failed)
                    {...(isImageLoaded && {
                        name: displayName || undefined,
                        image: {
                            // include version in key to force re-render if same URL string reused
                            src: photoUrl || undefined,
                        },
                    })}
                />
            </button>
        </Tooltip>
    );
};

export const MemoizedMeControlLauncher: React.NamedExoticComponent<MeControlLauncherProps> =
    React.memo(MeControlLauncher);
